import React, { useState, useContext } from 'react';
import { DialogContent, Divider, Grid, Select, MenuItem, TextField } from '@mui/material';
import { MarkStateContext } from '../../context/MarkContext';
import useGlynt from '../../components/hooks/useGlynt';
import { useCreateCorrection } from '../../components/hooks/useCreateCorrection';
import { AppContext } from '../../App';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { IRevertData, IBulkCorrectionSummary } from './SpreadsheetTypes';
import {
  showUpdateAllExtractionsModalAtom,
  bulkCorrectionRevertDataAtom,
  bulkCorrectionSummaryAtom,
  filteredExtractionIdsAtom,
} from '../../atoms/spreadsheet-atoms';
import { useAtom, useAtomValue } from 'jotai';
import DialogGlynt, { FormLabelStyled, FormControlStyled } from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import { useParams } from 'react-router';
import useGetExtractionBatch from '../hooks/useGetExtractionBatch';
import { getIdFromUrl } from '../../utils/getIdFromUrl';

const UpdateAllExtractionsDialog = () => {
  const { dpid, id, eid } = useParams<{
    dpid: string;
    id: string;
    eid: string;
  }>();

  if (!dpid || !id) return null;

  const [showUpdateAllExtractionsModal, setShowUpdateAllExtractionsModal] = useAtom(
    showUpdateAllExtractionsModalAtom
  );
  const [, setBulkCorrectionRevertData] = useAtom(bulkCorrectionRevertDataAtom);
  const [, setBulkCorrectionSummary] = useAtom(bulkCorrectionSummaryAtom);
  const filteredExtractionIds = useAtomValue(filteredExtractionIdsAtom);

  const { spreadsheetFocusedCell } = useContext(MarkStateContext);
  const { setToast } = useContext(AppContext);

  const [loadingSubmittingCorrections, setLoadingSubmittingCorrections] = useState(false);

  const {
    data: extractionBatch,
    isLoading: isLoadingExtractionBatch,
    isFetching: isFetchingExtractionBatch,
  } = useGetExtractionBatch(dpid, id, true);

  // Use filtered extraction IDs from the grid, or fall back to all extractions when no filters are applied
  const extractionIdsToUse =
    filteredExtractionIds.length > 0
      ? filteredExtractionIds
      : (extractionBatch?.extractions.map((extraction) => getIdFromUrl(extraction)) ?? []);

  const [updateText, setUpdateText] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [updateMode, setUpdateMode] = useState<
    | 'replace'
    | 'append'
    | 'prepend'
    | 'clear'
    | 'findReplace'
    | 'originalExtractedValue'
    | undefined
  >('replace');

  const glynt = useGlynt();

  const queryClient = useQueryClient();

  // Fetch corrected results for either the visible or all extractions
  const extractionQueries = useQueries({
    queries: extractionIdsToUse.map(extractionId => ({
      queryKey: ['extraction', extractionId, {
        view: 'corrected'
      }],

      queryFn: async () => (await glynt).extraction.retrieve_corrected_results(extractionId),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false
    }))
  });

  // Only get raw results when the user selects "originally extracted"
  const allRawResults = useQueries({
    queries: extractionIdsToUse.map(extractionId => ({
      queryKey: ['extraction', extractionId, {
        view: 'raw'
      }],

      queryFn: async () => (await glynt).extraction.retrieve_raw_result(extractionId),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      enabled: updateMode === 'originalExtractedValue'
    }))
  });

  const isLoadingExtractionQueries = extractionQueries.some((query) => query.isLoading);
  const isFetchingExtractionQueries = extractionQueries.some((query) => query.isFetching);

  const isLoadingAllRawResults = allRawResults.some((query) => query.isLoading);
  const isFetchingAllRawResults = allRawResults.some((query) => query.isFetching);

  const isLoading =
    isLoadingExtractionQueries || isLoadingExtractionBatch || isLoadingAllRawResults;
  const isFetching =
    isFetchingExtractionQueries || isFetchingExtractionBatch || isFetchingAllRawResults;

  const isError = extractionQueries.some((query) => query.isError);

  const correctedResults = extractionQueries.map((query) => query.data).filter(Boolean);
  const rawResults = allRawResults.map((query) => query.data).filter(Boolean);

  const createCorrectionMutation = useCreateCorrection(
    true,
    false,
    {
      onSuccess: () => {
        setToast({
          variant: 'success',
          open: true,
          message: 'Corrections updated successfully',
        });
      },
      onError: (error) => {
        setToast({
          variant: 'error',
          open: true,
          message: 'Failed to update corrections',
        });
        setLoadingSubmittingCorrections(false);
        queryClient.invalidateQueries({
          queryKey: ['extraction']
        });
      },
    },
    false
  );

  const closeModal = () => {
    setUpdateText('');
    setFindText('');
    setReplaceText('');
    setShowUpdateAllExtractionsModal(false);
  };

  const handleSubmit = async () => {
    if (isLoading || isLoadingExtractionBatch || isError || correctedResults.length === 0) {
      setToast({
        variant: 'error',
        open: true,
        message: 'Please wait for all extractions to load.',
      });
      return;
    }

    setLoadingSubmittingCorrections(true);
    setBulkCorrectionSummary([]); // Reset summary at start of new operation

    // Collect all revert data first
    const newRevertData: IRevertData[] = [];
    const correctionsSummary: IBulkCorrectionSummary[] = [];

    try {
      // Process each extraction
      for (let i = 0; i < correctedResults.length; i++) {
        const extraction = correctedResults[i];
        const documentId = getIdFromUrl(extraction?.document);

        if (!extraction?.id || !extraction?.results) {
          correctionsSummary.push({
            documentIndex: i,
            documentId: documentId,
            extractionId: extraction?.id || 'unknown',
            status: 'error',
            error: 'Missing extraction data',
          });
          continue;
        }

        // Get all fields from the extraction
        const fields = Object.entries(extraction.results || {});

        // find the field we need to update
        const fieldToUpdate = fields.find(
          ([fieldName]) => fieldName === spreadsheetFocusedCell?.colId
        );

        let fieldName = fieldToUpdate?.[0] || spreadsheetFocusedCell?.colId || '';
        let fieldData = fieldToUpdate?.[1] || { content: '', tokens: [] };

        const currentContent = fieldData?.content || '';

        let newContent = '';

        // Apply the update based on selected mode
        switch (updateMode) {
          case 'replace':
            newContent = updateText;
            break;
          case 'prepend':
            newContent = updateText + (currentContent ? currentContent : '');
            break;
          case 'append':
            newContent = (currentContent ? currentContent : '') + updateText;
            break;
          case 'clear':
            newContent = '';
            break;
          case 'findReplace':
            newContent = currentContent.replaceAll(findText, replaceText);
            break;
          case 'originalExtractedValue':
            const originalExtraction = rawResults.find((result) => result?.id === extraction.id);

            if (originalExtraction?.results) {
              const originalField = originalExtraction.results[fieldName];
              newContent = originalField?.content || '';
            }
            break;
          default:
            newContent = updateText;
        }

        // Only create correction if content has changed
        if (newContent !== currentContent) {
          // Store revert data
          newRevertData.push({
            extractionId: extraction.id,
            documentId: documentId,
            field: fieldName,
            originalContent: currentContent,
            originalTokens: fieldData?.tokens || [],
            timestamp: new Date().toISOString(),
          });

          try {
            await createCorrectionMutation.mutateAsync({
              extraction: extraction.id,
              field: fieldName,
              correction: {
                content: newContent,
                tokens: updateMode === 'clear' ? [] : fieldData?.tokens || [],
              },
            });

            correctionsSummary.push({
              documentIndex: i,
              documentId: documentId,
              extractionId: extraction.id,
              status: 'success',
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            correctionsSummary.push({
              documentIndex: i,
              documentId: documentId,
              extractionId: extraction.id,
              status: 'error',
              error: errorMessage,
            });

            // Show error toast but continue processing
            setToast({
              variant: 'error',
              open: true,
              message: `Error updating document ${i + 1}: ${errorMessage}`,
            });
          }
        } else {
          // No change needed, but still record as success
          correctionsSummary.push({
            documentIndex: i,
            documentId: documentId,
            extractionId: extraction.id,
            status: 'success',
          });
        }
      }

      // Update the correction summary
      setBulkCorrectionSummary(correctionsSummary);

      // Check if we had any successful updates and any errors
      const hasSuccessfulUpdates = correctionsSummary.some((status) => status.status === 'success');
      const hasErrors = correctionsSummary.some((status) => status.status === 'error');

      if (hasSuccessfulUpdates) {
        setBulkCorrectionRevertData(newRevertData);
        if (hasErrors) {
          // If we had both successes and errors, show a mixed message
          setToast({
            variant: 'warning',
            open: true,
            message:
              'Some corrections were updated successfully, but some failed. Check the status below.',
          });
        } else {
          // If all were successful, show success message and close dialog
          setToast({
            variant: 'success',
            open: true,
            message: 'All corrections were updated successfully',
          });
        }
      } else {
        // If all failed, show error message
        setToast({
          variant: 'error',
          open: true,
          message: 'No corrections were updated successfully',
        });
      }
      closeModal();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setToast({
        variant: 'error',
        open: true,
        message: `Error during bulk update: ${errorMessage}`,
      });
    } finally {
      setLoadingSubmittingCorrections(false);
    }
  };

  return (
    <>
      <DialogGlynt
        open={showUpdateAllExtractionsModal}
        closeModal={closeModal}
        width={'400px'}
        loading={loadingSubmittingCorrections}
        cypressId="dialog-update-all-extractions"
      >
        <DialogTitleGlynt
          title="Update All Extractions"
          closeModal={closeModal}
          startIcon="update"
          loading={loadingSubmittingCorrections}
        />
        <DialogContentGlynt>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <FormControlStyled fullWidth>
                <FormLabelStyled>UPDATE TYPE</FormLabelStyled>
                <Select
                  id="update-mode-select"
                  variant="outlined"
                  data-cy="select-update-mode"
                  disabled={loadingSubmittingCorrections}
                  size="small"
                  value={updateMode}
                  onChange={(e) =>
                    setUpdateMode(
                      e.target.value as 'replace' | 'append' | 'prepend' | 'clear' | 'findReplace'
                    )
                  }
                >
                  <MenuItem value="replace">Set Text To</MenuItem>
                  <MenuItem value="prepend">Prepend To Existing</MenuItem>
                  <MenuItem value="append">Append To Existing</MenuItem>
                  <MenuItem value="clear">Clear Out Field</MenuItem>
                  <MenuItem value="findReplace">Find and Replace</MenuItem>
                  <Divider />
                  <MenuItem value="originalExtractedValue">Original Extracted Value</MenuItem>
                </Select>
              </FormControlStyled>
            </Grid>

            {updateMode === 'findReplace' ? (
              <Grid item xs={12}>
                <FormControlStyled fullWidth>
                  <FormLabelStyled>FIND</FormLabelStyled>
                  <TextField
                    fullWidth
                    id="batch-name"
                    disabled={loadingSubmittingCorrections}
                    variant="outlined"
                    data-cy="input-update-find"
                    autoComplete="off"
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  />
                </FormControlStyled>

                <FormControlStyled fullWidth>
                  <FormLabelStyled>REPLACE WITH</FormLabelStyled>
                  <TextField
                    fullWidth
                    id="batch-name"
                    variant="outlined"
                    data-cy="input-update-replace-with"
                    autoComplete="off"
                    disabled={loadingSubmittingCorrections}
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  />
                </FormControlStyled>
              </Grid>
            ) : updateMode !== 'clear' && updateMode !== 'originalExtractedValue' ? (
              <Grid item xs={12}>
                <FormControlStyled fullWidth>
                  <FormLabelStyled>TEXT</FormLabelStyled>
                  <TextField
                    fullWidth
                    data-cy="input-update-text"
                    id="batch-name"
                    variant="outlined"
                    autoComplete="off"
                    disabled={loadingSubmittingCorrections}
                    value={updateText}
                    onChange={(e) => setUpdateText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  />
                </FormControlStyled>
              </Grid>
            ) : null}
          </Grid>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          showClearButton={false}
          cancelCypressId="btn-bulk-update-dialog-cancel"
          submitCypressId="btn-bulk-update-dialog-submit"
          clearCypressId="btn-bulk-update-dialog-clear"
          submitLoading={loadingSubmittingCorrections || isLoading || isFetching}
        />
      </DialogGlynt>
    </>
  );
};

export default UpdateAllExtractionsDialog;
